import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useXRModel, XR_PALETTE } from "./xrAssets";
import { Blob } from "./SceneEnv";

/** Animated character (Quest GLB) facing the patient; plays `clip` then falls back to idle. */
export const XRCharacter = ({ name, height, prop, clip = "idle", position, face = [0, 1.2, 0] }) => {
  const m = useXRModel(name, height, prop);
  const mixer = useMemo(() => m && new THREE.AnimationMixer(m.scene), [m]);
  useEffect(() => {
    if (!m) return;
    const a = m.clips[clip] || m.clips.idle;
    if (!a) return;
    const act = mixer.clipAction(a).reset().fadeIn(0.3).play();
    if (clip !== "idle" && m.clips.idle) {
      act.setLoop(THREE.LoopOnce, 1); act.clampWhenFinished = true;
      const done = () => { act.fadeOut(0.4); mixer.clipAction(m.clips.idle).reset().fadeIn(0.4).play(); };
      mixer.addEventListener("finished", done);
      return () => { mixer.removeEventListener("finished", done); act.fadeOut(0.3); };
    }
    return () => act.fadeOut(0.3);
  }, [m, mixer, clip]);
  useEffect(() => { if (m) m.root.lookAt(face[0], 0, face[2]); }, [m, face]);
  useFrame((_, dt) => { mixer?.update(Math.min(dt, 0.05)); m?.scene.userData.updateProp?.(); });
  return (
    <group position={position}>
      {m && <primitive object={m.root} />}
      <Blob r={height * 0.28} />
    </group>
  );
};

const ProceduralBaobab = () => {
  const trunk = useMemo(() => new THREE.LatheGeometry([[0, 0], [1.25, 0], [1.15, 0.6], [1.35, 1.8], [1.2, 3.2], [0.8, 4.4], [0.55, 5.1], [0, 5.2]].map(([x, y]) => new THREE.Vector2(x, y)), 28), []);
  const crowns = [[0, 6, 0, 1.9], [-1.8, 5.5, 0.3, 1.4], [1.9, 5.6, -0.2, 1.5], [0.4, 5.4, 1.6, 1.3], [-0.5, 5.6, -1.6, 1.3]];
  return (
    <group>
      <mesh geometry={trunk} castShadow receiveShadow><meshStandardMaterial color="#8C7563" roughness={0.85} /></mesh>
      {crowns.map(([x, y, z, r], i) => <mesh key={i} position={[x, y, z]} castShadow><icosahedronGeometry args={[r, 2]} /><meshStandardMaterial color={i % 2 ? "#3F8A5A" : "#4E9A62"} roughness={0.8} flatShading /></mesh>)}
    </group>
  );
};

/** Hero Great Baobab (Meshy GLB, Pixar-like) at the heart of the village; procedural stand-in until exported. */
export const HeroBaobab = ({ position = [0, 0, -9], height = 7.5 }) => {
  const m = useXRModel("baobab", height);
  useEffect(() => { m?.scene.traverse((o) => { if (o.isMesh) { o.castShadow = true; o.receiveShadow = true; } }); }, [m]);
  return (
    <group position={position}>
      {m ? <primitive object={m.root} /> : m === null && <group scale={height / 7.6}><ProceduralBaobab /></group>}
      <pointLight position={[0, 3, 3]} color={XR_PALETTE.gold} intensity={6} distance={9} decay={2} />
    </group>
  );
};
